import React, {
  useState,
  forwardRef,
  useImperativeHandle,
  useRef,
} from "react";
import { Box, Typography, Alert, CircularProgress, Chip } from "@mui/material";
import { Error as ErrorIcon } from "@mui/icons-material";
import { useAuth } from "../../contexts/AuthContext";
import { useNotification } from "../../hooks/useNotification";
import {
  FileUploadDialog,
  type ArtifactType as FileDialogArtifactType,
} from "./FileUploadDialog";
import {
  FileList,
  type FileItem,
  type FileListCallbacks,
  type FileListConfig,
} from "./fileList";
import notesIcon from "../../assets/notes.svg";
import { deliverableChipStyles } from "../../styles/chips";
import {
  defaultApiClient,
  useCompleteJob,
  useUndoCompleteJob,
} from "@taxassist/api-client";

// Use the artifact type from FileUploadDialog to avoid duplication
type ArtifactType = FileDialogArtifactType;

interface ProcessingArtifactsProps {
  files?: FileItem[];
  isLoading?: boolean;
  error?: Error | null;
  status: "draft" | "submitted" | "triage" | "processing" | "done" | "error";
  onDownload?: (file: FileItem) => Promise<void>;
  onPreview?: (file: FileItem, index: number) => void;
  onDelete?: (file: FileItem) => Promise<void>;
  getFileUrl?: (file: FileItem) => Promise<string>;
  onUpload?: (
    file: File,
    artifactType: ArtifactType,
    description?: string,
  ) => Promise<void>;
  showUpload?: boolean;
  isOpsContext?: boolean;
  surePrepJobId?: string;
  embedded?: boolean;
  jobId?: string;
  onArtifactUpdate?: () => void;
  onDownloadAll?: () => void;
  downloadingAll?: boolean;
}

// Imperative handle for parent components to trigger upload
export interface ProcessingArtifactsRef {
  triggerUpload: () => void;
}

export const ProcessingArtifacts = forwardRef<
  ProcessingArtifactsRef,
  ProcessingArtifactsProps
>(
  (
    {
      files = [],
      isLoading = false,
      error = null,
      status,
      jobId,
      onDownload,
      onPreview,
      onDelete,
      getFileUrl,
      onUpload,
      showUpload,
      isOpsContext = false,
      surePrepJobId,
      embedded: _embedded = false,
      onArtifactUpdate,
      onDownloadAll: _externalDownloadAll,
      downloadingAll: _externalDownloadingAll,
    },
    ref,
  ) => {
    const { user } = useAuth();

    // State for the new upload dialog
    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);

    // Notification system
    const notification = useNotification();

    // Expose triggerUpload function to parent components
    useImperativeHandle(ref, () => ({
      triggerUpload: () => {
        setUploadDialogOpen(true);
      },
    }));

    // Ref to the panel container to host inline toasts within this right-side panel
    const panelContainerRef = useRef<HTMLDivElement | null>(null);

    // Create callbacks for the FileList component
    const callbacks: FileListCallbacks = {
      onPreview: onPreview,
      onDownload: onDownload,
      onDelete: onDelete,
      getFileUrl: getFileUrl,
      onUpdateFileType: isOpsContext
        ? async (file, newType) => {
            try {
              // Extract jobId and artifactId from current props and file
              const artifactId = file.id;
              if (!jobId) return;
              await defaultApiClient({
                url: `/ops/jobs/${jobId}/processing-results/${artifactId}`,
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                data: { artifactType: newType },
              });
              notification.showSuccess("File type updated", {
                anchorOrigin: { vertical: "bottom", horizontal: "center" },
              });
              onArtifactUpdate?.();
            } catch (err) {
              console.error("Failed to update processing result type", err);
              notification.showError("Failed to update file type", {
                anchorOrigin: { vertical: "bottom", horizontal: "center" },
              });
              throw err;
            }
          }
        : undefined,
    };

    // Determine if delete should be allowed for admins and reviewers
    const shouldAllowDelete = !!(
      onDelete &&
      user?.role &&
      (user.role === "admin" || user.role === "reviewer")
    );

    // Configuration for the FileList component
    const config: FileListConfig = {
      showActions: true,
      compact: true,
      allowDelete: shouldAllowDelete,
      enableViewer: !!getFileUrl,
      emptyMessage: "No processing results yet",
      showTypeBadges: true, // Processing artifacts show type badges
      readOnlyTypeBadges: !isOpsContext, // Only ops can edit type badges
    };

    // Determine if upload should be shown based on role and context
    const shouldShowUpload =
      showUpload ??
      (isOpsContext &&
        user?.role &&
        (user.role === "admin" || user.role === "reviewer") &&
        onUpload);

    // Complete job mutation
    const completeJobMutation = useCompleteJob({
      mutation: {
        onSuccess: () => {
          // Reset loading state and show success toast with undo button
          notification.updateActionLoading(false);
          notification.showSuccess("Job completed", {
            anchorOrigin: { vertical: "bottom", horizontal: "center" },
            duration: 20000,
            action: {
              label: "UNDO",
              onClick: handleUndoComplete,
            },
          });
          onArtifactUpdate?.();
        },
        onError: (error: any) => {
          console.error("Complete job failed:", error);
          notification.updateActionLoading(false);
          notification.showError(error?.message || "Failed to complete job", {
            anchorOrigin: { vertical: "bottom", horizontal: "center" },
          });
        },
      },
    });

    // Undo complete job mutation
    const undoCompleteJobMutation = useUndoCompleteJob({
      mutation: {
        onSuccess: () => {
          notification.updateActionLoading(false);
          notification.showSuccess("Job completion undone", {
            anchorOrigin: { vertical: "bottom", horizontal: "center" },
            duration: 4000, // 4 seconds
          });
          onArtifactUpdate?.();
        },
        onError: (error: any) => {
          console.error("Undo complete job failed:", error);
          notification.updateActionLoading(false);
          notification.showError(
            error?.message || "Failed to undo job completion",
            {
              anchorOrigin: { vertical: "bottom", horizontal: "center" },
            },
          );
        },
      },
    });

    // Handle complete job action
    const handleCompleteJob = () => {
      if (!jobId) {
        console.error("Cannot complete job: jobId is missing");
        return;
      }

      // Set loading state on the notification action button
      notification.updateActionLoading(true);
      completeJobMutation.mutate({ jobId });
    };

    // Handle undo complete action
    const handleUndoComplete = () => {
      if (!jobId) {
        console.error("Cannot undo complete job: jobId is missing");
        return;
      }

      // Set loading state on the notification action button
      notification.updateActionLoading(true);
      undoCompleteJobMutation.mutate({ jobId });
    };

    // Handle upload through the new dialog
    const handleUpload = async (
      file: File,
      artifactType: ArtifactType,
      description?: string,
    ) => {
      if (!onUpload) return;

      try {
        await onUpload(file, artifactType, description);
        setUploadDialogOpen(false);
        onArtifactUpdate?.();
        notification.showSuccess("Deliverable uploaded", {
          anchorOrigin: { vertical: "bottom", horizontal: "center" },
          duration: 20000, // 20 seconds to allow time to click complete job
          action: {
            label: "DELIVERABLES READY",
            onClick: handleCompleteJob,
          },
        });
      } catch (error) {
        console.error("Upload failed:", error);
        notification.showError(
          error instanceof Error ? error.message : "Upload failed",
          {
            anchorOrigin: { vertical: "bottom", horizontal: "center" },
          },
        );
        throw error; // Re-throw so dialog can handle it
      }
    };

    const handleUploadError = (error: Error) => {
      console.error("Upload error:", error);
      notification.showError(error.message, {
        anchorOrigin: { vertical: "bottom", horizontal: "center" },
      });
    };

    // Show loading state while fetching processing results
    if (isLoading) {
      const loadingContent = (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <CircularProgress size={40} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Loading processing results...
          </Typography>
        </Box>
      );

      return loadingContent;
    }

    // Show error state if processing results failed to load
    if (error) {
      console.error("Failed to load processing results:", error);
      const errorContent = (
        <Alert severity="error">
          Failed to load processing results. Please try refreshing the page.
        </Alert>
      );

      return errorContent;
    }

    // Determine the main content to show based on job status
    let mainContent: React.ReactNode;

    if (status === "error") {
      // For client portal, show "processing in progress" instead of error details
      if (!isOpsContext) {
        // Treat as processing in progress for client-facing view
        mainContent = (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Box
              sx={{
                fontSize: "3rem",
                mb: 2,
              }}
            >
              <img
                src={notesIcon}
                alt="notes"
                style={{ width: 48, height: 48 }}
              />
            </Box>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
              Processing in progress
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Results will be available once processing is complete.
            </Typography>
          </Box>
        );
      } else {
        // For ops context, show error details and any uploaded processing results
        mainContent = (
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <ErrorIcon color="error" />
              <Typography variant="subtitle1">Processing Failed</Typography>
            </Box>
            <Alert severity="error" sx={{ mb: 2 }}>
              The tax return processing failed. Please review the uploaded
              documents and try again, or contact support for assistance.
            </Alert>

            {/* Show processing results if any exist */}
            {files.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 2 }}>
                  Uploaded Processing Results ({files.length})
                </Typography>

                <FileList files={files} callbacks={callbacks} config={config} />
              </Box>
            )}

            {surePrepJobId && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                SurePrep Job ID: {surePrepJobId}
              </Typography>
            )}
          </Box>
        );
      }
    } else if (status !== "done") {
      // Show processing/waiting content with any uploaded processing results for ops users
      mainContent = (
        <Box>
          {/* Processing Status Header */}
          {!files ||
            (files.length == 0 && (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Box
                  sx={{
                    fontSize: "3rem",
                    mb: 2,
                  }}
                >
                  <img
                    src={notesIcon}
                    alt="notes"
                    style={{ width: 48, height: 48 }}
                  />
                </Box>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mb: 1, fontWeight: 500 }}
                >
                  {isOpsContext ? (
                    <>
                      <Box
                        component="span"
                        sx={{
                          textDecoration: "underline",
                          cursor: "pointer",
                          "&:hover": {
                            color: "primary.main",
                          },
                        }}
                        onClick={() => setUploadDialogOpen(true)}
                      >
                        Upload deliverables
                      </Box>{" "}
                      to complete this job.
                    </>
                  ) : (
                    "Processing in progress"
                  )}
                </Typography>
                {status === "draft" ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: shouldShowUpload ? 3 : 0 }}
                  >
                    Results will be available after the job is submitted and
                    processed.
                  </Typography>
                ) : shouldShowUpload ? (
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      flexWrap: "wrap",
                      mb: 3,
                      justifyContent: "center",
                    }}
                  >
                    <Chip
                      label="BINDER"
                      size="small"
                      sx={deliverableChipStyles}
                    />
                    <Chip
                      label="K1X IMPORT"
                      size="small"
                      sx={deliverableChipStyles}
                    />
                    <Chip
                      label="TAX SOFTWARE IMPORT"
                      size="small"
                      sx={deliverableChipStyles}
                    />
                  </Box>
                ) : (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: shouldShowUpload ? 3 : 0 }}
                  >
                    Results will be available once processing is complete.
                  </Typography>
                )}

                {surePrepJobId && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 2, display: "block" }}
                  >
                    SurePrep Job ID: {surePrepJobId}
                  </Typography>
                )}
              </Box>
            ))}

          {/* Show uploaded processing results if any exist (ops context only) */}
          {files.length > 0 && isOpsContext && (
            <Box sx={{ mt: 3, pt: 3, borderTop: 1, borderColor: "divider" }}>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                Uploaded Processing Results ({files.length})
              </Typography>

              <FileList files={files} callbacks={callbacks} config={config} />
            </Box>
          )}
        </Box>
      );
    } else {
      // Show completed results
      mainContent = (
        <>
          <Alert severity="success" sx={{ mt: 2, mb: 2 }}>
            Your tax return has been successfully processed!
          </Alert>

          <FileList files={files} callbacks={callbacks} config={config} />

          {surePrepJobId && (
            <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: "divider" }}>
              <Typography variant="caption" color="text.secondary">
                SurePrep Job ID: {surePrepJobId}
              </Typography>
            </Box>
          )}
        </>
      );
    }

    return (
      <>
        <Box ref={panelContainerRef} sx={{ position: "relative" }}>
          {mainContent}
        </Box>

        {/* Processing Result Upload Dialog */}
        {shouldShowUpload && (
          <FileUploadDialog
            mode="processing-results"
            open={uploadDialogOpen}
            onClose={() => setUploadDialogOpen(false)}
            jobId={jobId}
            onProcessingUpload={handleUpload}
            onError={handleUploadError}
          />
        )}

        {/* Notification Component */}
        {notification.NotificationComponent}
      </>
    );
  },
);

ProcessingArtifacts.displayName = "ProcessingArtifacts";
